import { useContext, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Switch } from 'antd';
import ProfitPoolbyHierarchyChart from './ProfitPoolbyHierarchyChart';
import ProfitPoolbyHierarchyTable from './ProfitPoolbyHierarchyTable';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
// import { profitPoolData } from './data/profirPoolData';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import { useProfitPoolOverviewContext } from './context/ProfitPoolOverviewContext';
import { startCase } from 'lodash';
import useProfitPoolbyHierarchyDataInfiniteScroll from './hooks/useProfitPoolbyHierarchyDataInfiniteScroll';
import CustomDropdownButton from 'components/atoms/CustomDropdownButton';
import DownSvg from 'assets/svg/Down';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PP_OVERVIEW_CHART_VIEW_TOGGLED, PP_OVERVIEW_UNIT_CHANGED } from 'utils/mixpanelEventNames';

const ProfitPoolbyHierarchy = () => {
    const [isChartEnabled, setIsChartEnabled] = useState(true);
    const currentRef = useRef<any>(null);

    const { appConfig } = useContext(UserContext);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { ppOverviewDropDownOptions, defaultCurrencySymbol, kpiLabelMapping } = profitPoolConfig;

    const ppOverviewUnitsList = useMemo(() => {
        return ppOverviewDropDownOptions.map((option: any) => {
            return {
                label: `${option.label.replace('$', defaultCurrencySymbol)}`,
                key: option.key,
            };
        });
    }, [ppOverviewDropDownOptions]);

    const { selecedPPOUnit, setSelecedPPOUnit, selectedSubFilter } = useProfitPoolOverviewContext();
    const {
        tableData: profitPoolTableDataFinal,
        tableDefinition: profitPoolTableDefinition,
        isLoading,
        isSuccess,
    } = useProfitPoolbyHierarchyDataInfiniteScroll();

    const excelExportData = useMemo(() => {
        if (!profitPoolTableDataFinal?.length || !profitPoolTableDefinition?.length) {
            return { headers: [], data: [] };
        }

        // Extract headers from table definition
        const headers = profitPoolTableDefinition.map((col: any) => col.title || col.dataIndex);

        // Transform data rows
        const data = profitPoolTableDataFinal.map((row: any) => {
            return profitPoolTableDefinition.map((col: any) => {
                const dataIndex = col.dataIndex;
                const value = row[dataIndex];
                const percentValue = row[`${dataIndex}Percent`];

                // For tier columns (non-attributeDesc), combine value and percentage if both exist
                if (dataIndex !== 'attributeDesc' && value && percentValue) {
                    return `${value} (${percentValue})`;
                }

                // For attributeDesc or if percentage doesn't exist, return just the value
                return value || '';
            });
        });

        return { headers, data };
    }, [profitPoolTableDataFinal, profitPoolTableDefinition]);

    return (
        <div>
            <div className="flex flex-row items-center justify-between">
                <p className="text-lg font-semibold">{`${kpiLabelMapping['profit']} Pool by ${startCase(
                    selectedSubFilter?.dataKey,
                )}`}</p>
                <div className="flex flex-row gap-x-2 items-center">
                    <div className="flex flex-row gap-1 items-center">
                        <p>{'Table'}</p>
                        <Switch
                            defaultChecked
                            onChange={() => {
                                trackEventMixpanel(PP_OVERVIEW_CHART_VIEW_TOGGLED, {
                                    from: isChartEnabled ? 'chart' : 'table',
                                    to: isChartEnabled ? 'table' : 'chart',
                                });
                                setIsChartEnabled(!isChartEnabled);
                            }}
                            disabled={isLoading}
                        />
                        <p>{'Chart'}</p>
                    </div>
                    <Dropdown
                        menu={{
                            items: ppOverviewUnitsList,
                            onClick: (info) => {
                                trackEventMixpanel(PP_OVERVIEW_UNIT_CHANGED, {
                                    selected_mode: ppOverviewUnitsList.find((option: any) => option.key === info.key)
                                        ?.label,
                                });
                                setSelecedPPOUnit(info.key);
                            },
                        }}
                        trigger={['click']}
                        placement="bottomRight"
                        disabled={isLoading}
                    >
                        <CustomDropdownButton type="default">
                            {ppOverviewUnitsList.find((option: any) => option.key === selecedPPOUnit)?.label}
                            <div className="ml-2">
                                <DownSvg />
                            </div>
                        </CustomDropdownButton>
                    </Dropdown>
                    {isSuccess && (
                        <div className="flex flex-row gap-2">
                            <div className="flex justify-end mb-3 mt-4">
                                <span className="flex gap-2">
                                    <CopyToClipBoard refOfComponent={currentRef} />
                                </span>
                            </div>
                            <div className="flex justify-end mb-3 mt-4">
                                <DownloadExcel
                                    title={`${kpiLabelMapping?.['profit']} Pool by ${startCase(
                                        selectedSubFilter?.dataKey,
                                    )}`}
                                    excelData={excelExportData.data}
                                    headers={excelExportData.headers}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Legend Section */}
            {isChartEnabled ? (
                <ProfitPoolbyHierarchyChart ref={currentRef} />
            ) : (
                <ProfitPoolbyHierarchyTable ref={currentRef} />
            )}
        </div>
    );
};

export default ProfitPoolbyHierarchy;
